// Global Instructions Rule Applied!
//
// Cap-table draft editor (Phase 4). Captures/edits a DRAFT snapshot: header
// (kind, round, as-of date, notes) + the full class-line set, each line with
// its sparse holder rows (linked account / plain text / "Other" aggregate; a
// line with no holders is class-totals-only). Our own positions are never
// typed here — they derive from asset deltas in the view. Saving keeps the
// draft; "Save & set current" promotes it (superseding the prior current).
// Account options for holder linking reuse the asset-transaction header
// options (all accounts) — same source as the booking form's Company select.
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Checkbox, Divider, Input, Modal, Select, Table } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import InputNumber from '../../../core/components/InputNumber.jsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import AddSecurityModal from '../../asset_transactions/components/AddSecurityModal.tsx'
import AddCompanyModal from '../../companies/components/AddCompanyModal.tsx'
import { useAccountOptions } from '../../asset_transactions/hooks/useAccountOptions.ts'
import {
  CapTableCompanyRound,
  CapTableCompanySecurity,
  CapTableRoundParticipation
} from '../../../state_manager/data_models/cap_table_model.ts'
import {
  CapTableSavePayload,
  saveCapTableVersion,
  setCapTableCurrent
} from '../services/dealExecutionService.ts'

const KIND_OPTIONS = [
  { value: 'round', label: 'Round' },
  { value: 'corporate_action', label: 'Corporate action' },
  { value: 'ad_hoc', label: 'Ad hoc' }
]

const LINE_KIND_OPTIONS = [
  { value: 'equity', label: 'Equity' },
  // Warrants are issued but not outstanding: their count feeds the fully
  // diluted total only, never the outstanding capitalisation.
  { value: 'warrant', label: 'Warrant' },
  { value: 'convertible', label: 'Convertible' },
  { value: 'debt', label: 'Debt' }
]

const HOLDER_KIND_OPTIONS = [
  { value: 'account', label: 'Linked account' },
  { value: 'text', label: 'Named (text)' },
  { value: 'other', label: 'Other (aggregate)' }
]

interface DraftHolder {
  key: string
  id?: string
  holder_kind: 'account' | 'text' | 'other'
  holder_account_id: string | null
  holder_name: string | null
  issued: number | null
  fully_diluted: number | null
}

interface DraftLine {
  key: string
  id?: string
  // Many-to-many: one class line may cover several securities.
  security_ids: string[]
  class_name: string
  issued: number | null
  fully_diluted: number | null
  issue_price: number | null
  is_esop_pool: boolean
  line_kind: 'equity' | 'warrant' | 'convertible' | 'debt'
  holders: DraftHolder[]
}

let keyCounter = 0
function nextKey(): string {
  keyCounter += 1
  return `k${keyCounter}`
}

function numberFromBlur(e: React.FocusEvent<HTMLInputElement>): number | null {
  const raw = e.target.value.replace(/[^0-9.-]/g, '')
  return raw === '' ? null : Number(raw)
}

interface CapTableEditorModalProps {
  open: boolean
  accountId: string
  initial: CapTableSavePayload | null
  companyRounds: CapTableCompanyRound[]
  companySecurities: CapTableCompanySecurity[]
  roundParticipations?: CapTableRoundParticipation[]
  onClose: () => void
  onSaved: (versionId: string) => void | Promise<void>
}

export default function CapTableEditorModal({
  open,
  accountId,
  initial,
  companyRounds,
  companySecurities,
  roundParticipations = [],
  onClose,
  onSaved
}: CapTableEditorModalProps): React.ReactElement {
  const [kind, setKind] = useState('round')
  const [roundId, setRoundId] = useState<string | null>(null)
  const [asOf, setAsOf] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([])
  const [saving, setSaving] = useState(false)
  // Holder-linking account options refresh whenever a holder dropdown opens
  // so accounts created elsewhere appear without a reload.
  const {
    options: accountOptions,
    refetch: refetchAccountOptions,
    onOpenRefetch: onAccountOpenRefetch
  } = useAccountOptions(open)
  // Inline account create from a holder row's dropdown: which holder opened it.
  const [addAccountFor, setAddAccountFor] = useState<{ lineKey: string; holderKey: string } | null>(
    null
  )
  // Target class line for the footer-level co-investor import (only shown
  // when several lines exist; defaults to the first equity line).
  const [importTargetKey, setImportTargetKey] = useState<string | null>(null)
  // Inline security create: which line's Securities select opened the modal,
  // plus securities created this session (merged into the options until the
  // next cap-table refetch returns them server-side).
  const [addSecurityForLineKey, setAddSecurityForLineKey] = useState<string | null>(null)
  const [extraSecurities, setExtraSecurities] = useState<Array<{ value: string; label: string }>>([])

  useEffect(() => {
    if (!open || !initial) return
    setKind(initial.kind || 'round')
    setRoundId(initial.round_id ?? null)
    setAsOf(initial.as_of_date ?? null)
    setNotes(initial.notes || '')
    setImportTargetKey(null)
    setAddSecurityForLineKey(null)
    setExtraSecurities([])
    setLines(
      (initial.lines || []).map((l) => ({
        key: nextKey(),
        id: l.id,
        security_ids: l.security_ids ?? [],
        class_name: l.class_name || '',
        issued: l.issued ?? null,
        fully_diluted: l.fully_diluted ?? null,
        issue_price: l.issue_price ?? null,
        is_esop_pool: l.is_esop_pool ?? false,
        line_kind: l.line_kind ?? 'equity',
        holders: (l.holders || []).map((h) => ({
          key: nextKey(),
          id: h.id,
          holder_kind: h.holder_kind,
          holder_account_id: h.holder_account_id ?? null,
          holder_name: h.holder_name ?? null,
          issued: h.issued ?? null,
          fully_diluted: h.fully_diluted ?? null
        }))
      }))
    )
  }, [open, initial])

  const securityOptions = useMemo(() => {
    const base = companySecurities.map((s) => ({
      value: s.securities__id,
      label: s.securities__name || 'Unnamed security'
    }))
    const known = new Set(base.map((o) => o.value))
    return [...base, ...extraSecurities.filter((o) => !known.has(o.value))]
  }, [companySecurities, extraSecurities])

  // A line adopts the security type's cap-table classification
  // (cfg.security_types.default_line_kind) when it gets its FIRST linked
  // security — a Warrant-type security must not silently sit on an equity
  // (outstanding) line. Later links and explicit kind picks are respected.
  const defaultKindFor = (securityId: string): DraftLine['line_kind'] | null => {
    const kind = companySecurities.find((s) => s.securities__id === securityId)
      ?.security_types__default_line_kind
    return kind === 'equity' || kind === 'warrant' || kind === 'convertible' || kind === 'debt'
      ? kind
      : null
  }

  const patchLineSecurities = (lineKey: string, securityIds: string[]): void => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.key !== lineKey) return l
        const adopted =
          l.security_ids.length === 0 && securityIds.length > 0
            ? defaultKindFor(securityIds[0])
            : null
        return { ...l, security_ids: securityIds, ...(adopted ? { line_kind: adopted } : {}) }
      })
    )
  }

  // Inline create from a holder dropdown: refetch options, then link the
  // new account into the holder row that opened the modal.
  const handleCompanyCreated = async (accountId: string): Promise<void> => {
    const target = addAccountFor
    setAddAccountFor(null)
    await refetchAccountOptions()
    if (target) {
      patchHolder(target.lineKey, target.holderKey, { holder_account_id: accountId })
    }
  }

  const handleSecurityCreated = (securityId: string, securityName?: string): void => {
    const lineKey = addSecurityForLineKey
    setAddSecurityForLineKey(null)
    setExtraSecurities((prev) => [...prev, { value: securityId, label: securityName || 'New security' }])
    if (lineKey) {
      const line = lines.find((l) => l.key === lineKey)
      patchLineSecurities(lineKey, [...(line?.security_ids ?? []), securityId])
    }
  }

  const roundOptions = useMemo(
    () =>
      companyRounds.map((r) => ({
        value: r.rounds__id,
        label: `${r.rounds__name || 'Unnamed round'}${
          r.rounds__round_close_date
            ? ` · ${dayjs(r.rounds__round_close_date).format('MM/DD/YYYY')}`
            : ''
        }`
      })),
    [companyRounds]
  )

  const patchLine = (key: string, updates: Partial<DraftLine>): void => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...updates } : l)))
  }

  const patchHolder = (lineKey: string, holderKey: string, updates: Partial<DraftHolder>): void => {
    setLines((prev) =>
      prev.map((l) =>
        l.key === lineKey
          ? {
              ...l,
              holders: l.holders.map((h) => (h.key === holderKey ? { ...h, ...updates } : h))
            }
          : l
      )
    )
  }

  const save = async (alsoSetCurrent: boolean): Promise<void> => {
    if (!asOf) {
      showErrorToast('As-of date is required')
      return
    }
    if (lines.some((l) => !l.class_name.trim())) {
      showErrorToast('Every class line needs a name')
      return
    }
    const badHolder = lines.some((l) =>
      l.holders.some(
        (h) =>
          (h.holder_kind === 'account' && !h.holder_account_id) ||
          (h.holder_kind === 'text' && !(h.holder_name || '').trim())
      )
    )
    if (badHolder) {
      showErrorToast('Linked holders need an account; named holders need a name')
      return
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const payload: CapTableSavePayload = {
        ...(initial?.id ? { id: initial.id } : {}),
        account_id: accountId,
        round_id: kind === 'round' ? roundId : null,
        kind,
        as_of_date: asOf,
        notes: notes || null,
        lines: lines.map((l, idx) => ({
          ...(l.id ? { id: l.id } : {}),
          security_ids: l.security_ids,
          class_name: l.class_name.trim(),
          issued: l.issued,
          fully_diluted: l.fully_diluted,
          issue_price: l.issue_price,
          is_esop_pool: l.is_esop_pool,
          line_kind: l.line_kind,
          sort_order: idx,
          holders: l.holders.map((h, hIdx) => ({
            ...(h.id ? { id: h.id } : {}),
            holder_kind: h.holder_kind,
            holder_account_id: h.holder_kind === 'account' ? h.holder_account_id : null,
            holder_name: h.holder_kind === 'text' ? h.holder_name : null,
            issued: h.issued,
            fully_diluted: h.fully_diluted,
            sort_order: hIdx
          }))
        }))
      }
      const result = await saveCapTableVersion(token, payload)
      if (!result.versionId) {
        showErrorToast(result.error || 'Failed to save cap-table version')
        return
      }
      if (alsoSetCurrent) {
        const promoted = await setCapTableCurrent(token, result.versionId)
        if (!promoted.success) {
          showErrorToast(promoted.error || 'Saved as draft, but failed to set current')
          await onSaved(result.versionId)
          return
        }
        toast.success('Cap-table version saved and set current')
      } else {
        toast.success('Cap-table draft saved')
      }
      await onSaved(result.versionId)
    } finally {
      setSaving(false)
    }
  }

  // Round co-investors not yet holders on this line — the import prefill
  // (owner plan 2026-08-13: shares = amount ÷ the line's issue price when
  // both are known; blank otherwise, to fill once the round prices).
  // ROUND-TIED versions only (owner 2026-08-14): the round's investors
  // belong to its own snapshot, not to corporate actions or ad-hoc views.
  const roundImportCandidates = (line: DraftLine): CapTableRoundParticipation[] => {
    if (kind !== 'round' || !roundId) return []
    return roundParticipations.filter(
      (p) =>
        p.rounds__id === roundId &&
        !line.holders.some(
          (h) =>
            (p.accounts__id && h.holder_account_id === p.accounts__id) ||
            (!p.accounts__id &&
              p.round_participations__name &&
              (h.holder_name || '').trim().toLowerCase() ===
                p.round_participations__name.trim().toLowerCase())
        )
    )
  }

  // Import into an existing line (targetKey) or, when the editor has no
  // lines yet (fresh capture), into a new class line created on the spot.
  const importRoundInto = (targetKey: string | null): void => {
    setLines((prev) => {
      let next = [...prev]
      let key = targetKey
      if (!key) {
        const fresh: DraftLine = {
          key: nextKey(),
          security_ids: [],
          class_name: '',
          issued: null,
          fully_diluted: null,
          issue_price: null,
          is_esop_pool: false,
          line_kind: 'equity',
          holders: []
        }
        next = [...next, fresh]
        key = fresh.key
      }
      return next.map((l) => {
        if (l.key !== key) return l
        const candidates = roundImportCandidates(l)
        return {
          ...l,
          holders: [
            ...l.holders,
            ...candidates.map((p) => {
              const shares =
                p.round_participations__amount != null && l.issue_price
                  ? Math.round(Number(p.round_participations__amount) / Number(l.issue_price))
                  : null
              return {
                key: nextKey(),
                holder_kind: (p.accounts__id ? 'account' : 'text') as DraftHolder['holder_kind'],
                holder_account_id: p.accounts__id ?? null,
                holder_name: p.accounts__id ? null : (p.round_participations__name ?? null),
                issued: shares,
                fully_diluted: shares
              }
            })
          ]
        }
      })
    })
  }

  // Soft over-allocation check (owner 2026-08-14): non-blocking — class
  // totals stay authoritative — but named holders summing past the class
  // total is almost certainly a typo.
  const holderOverAllocation = (line: DraftLine): string | null => {
    const sumIssued = line.holders.reduce((n, h) => n + (h.issued || 0), 0)
    const sumFd = line.holders.reduce((n, h) => n + (h.fully_diluted || 0), 0)
    const overIssued = line.issued != null && sumIssued > Number(line.issued)
    const overFd = line.fully_diluted != null && sumFd > Number(line.fully_diluted)
    if (!overIssued && !overFd) return null
    const parts: string[] = []
    if (overIssued) parts.push(`issued ${sumIssued.toLocaleString('en-US')} vs class ${Number(line.issued).toLocaleString('en-US')}`)
    if (overFd) parts.push(`FD ${sumFd.toLocaleString('en-US')} vs class ${Number(line.fully_diluted).toLocaleString('en-US')}`)
    return `Holders exceed the class total (${parts.join('; ')}).`
  }

  const holdersEditor = (line: DraftLine): React.ReactElement => (
    <div className='py-1'>
      {holderOverAllocation(line) && (
        <div className='text-xs text-amber-600 mb-1'>{holderOverAllocation(line)}</div>
      )}
      <Table
        size='small'
        rowKey='key'
        pagination={false}
        dataSource={line.holders}
        locale={{ emptyText: 'No holder detail — class totals only' }}
        columns={[
          {
            title: 'Kind',
            dataIndex: 'holder_kind',
            width: 150,
            render: (v, h) => (
              <Select
                size='small'
                className='w-full'
                value={v}
                options={HOLDER_KIND_OPTIONS}
                onChange={(value) =>
                  patchHolder(line.key, h.key, {
                    holder_kind: value as DraftHolder['holder_kind'],
                    ...(value !== 'account' ? { holder_account_id: null } : {}),
                    ...(value !== 'text' ? { holder_name: null } : {})
                  })
                }
              />
            )
          },
          {
            title: 'Holder',
            key: 'holder',
            render: (_, h) => {
              if (h.holder_kind === 'account') {
                return (
                  <Select
                    size='small'
                    className='w-full'
                    showSearch
                    optionFilterProp='label'
                    placeholder='Select account…'
                    value={h.holder_account_id ?? undefined}
                    options={accountOptions}
                    onDropdownVisibleChange={onAccountOpenRefetch}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '4px 0' }} />
                        <Button
                          type='text'
                          block
                          size='small'
                          icon={<PlusOutlined />}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setAddAccountFor({ lineKey: line.key, holderKey: h.key })}
                        >
                          New account…
                        </Button>
                      </>
                    )}
                    onChange={(value) => patchHolder(line.key, h.key, { holder_account_id: value })}
                  />
                )
              }
              if (h.holder_kind === 'other') {
                return <span className='text-gray-400 text-xs'>Aggregate of remaining holders</span>
              }
              return (
                <Input
                  size='small'
                  placeholder='Holder name'
                  value={h.holder_name || ''}
                  onChange={(e) => patchHolder(line.key, h.key, { holder_name: e.target.value })}
                />
              )
            }
          },
          {
            title: 'Issued',
            dataIndex: 'issued',
            width: 130,
            render: (v, h) => (
              <InputNumber
                size='small'
                className='w-full'
                value={v ?? undefined}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                  patchHolder(line.key, h.key, { issued: numberFromBlur(e) })
                }
              />
            )
          },
          {
            title: 'FD',
            dataIndex: 'fully_diluted',
            width: 130,
            render: (v, h) => (
              <InputNumber
                size='small'
                className='w-full'
                value={v ?? undefined}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                  patchHolder(line.key, h.key, { fully_diluted: numberFromBlur(e) })
                }
              />
            )
          },
          {
            title: '',
            key: 'remove',
            width: 40,
            render: (_, h) => (
              <Button
                size='small'
                type='text'
                icon={<DeleteOutlined />}
                onClick={() =>
                  patchLine(line.key, { holders: line.holders.filter((x) => x.key !== h.key) })
                }
              />
            )
          }
        ]}
      />
      <Button
        size='small'
        type='dashed'
        icon={<PlusOutlined />}
        className='mt-2'
        onClick={() =>
          patchLine(line.key, {
            holders: [
              ...line.holders,
              {
                key: nextKey(),
                holder_kind: 'account',
                holder_account_id: null,
                holder_name: null,
                issued: null,
                fully_diluted: null
              }
            ]
          })
        }
      >
        Add holder
      </Button>
      {roundImportCandidates(line).length > 0 && (
        <Button
          size='small'
          type='dashed'
          className='mt-2 ml-2'
          onClick={() => importRoundInto(line.key)}
        >
          Import round co-investors ({roundImportCandidates(line).length})
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      title={initial?.id ? 'Edit Cap Table Version' : 'New Cap Table Version'}
      open={open}
      onCancel={onClose}
      width={1100}
      footer={[
        <Button key='cancel' onClick={onClose}>
          Cancel
        </Button>,
        <Button key='draft' loading={saving} onClick={() => save(false)}>
          Save draft
        </Button>,
        <Button key='current' type='primary' loading={saving} onClick={() => save(true)}>
          Save &amp; set current
        </Button>
      ]}
    >
      <div className='space-y-3 py-2'>
        <div className='grid grid-cols-2 xl:grid-cols-4 gap-3'>
          <div>
            <div className='text-xs text-gray-500 mb-1'>Kind</div>
            <Select size='small' className='w-full' value={kind} options={KIND_OPTIONS} onChange={setKind} />
          </div>
          <div>
            <div className='text-xs text-gray-500 mb-1'>Round</div>
            <Select
              size='small'
              className='w-full'
              allowClear
              disabled={kind !== 'round'}
              placeholder='Tie to a round…'
              value={roundId ?? undefined}
              options={roundOptions}
              onChange={(v) => setRoundId(v ?? null)}
            />
          </div>
          <div>
            <div className='text-xs text-gray-500 mb-1'>As of</div>
            <DatePicker
              size='small'
              className='w-full'
              format='MM/DD/YYYY'
              value={asOf ? dayjs(asOf) : null}
              onChange={(v) => setAsOf(v ? v.format('YYYY-MM-DD') : null)}
            />
          </div>
          <div>
            <div className='text-xs text-gray-500 mb-1'>Notes</div>
            <Input size='small' value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <Table
          size='small'
          rowKey='key'
          pagination={false}
          dataSource={lines}
          expandable={{ expandedRowRender: holdersEditor }}
          locale={{ emptyText: 'No class lines yet — add the share classes below' }}
          columns={[
            {
              title: 'Class',
              dataIndex: 'class_name',
              render: (v, line) => (
                <Input
                  size='small'
                  value={v}
                  placeholder='e.g. Series A Preferred'
                  onChange={(e) => patchLine(line.key, { class_name: e.target.value })}
                />
              )
            },
            {
              title: 'Kind',
              dataIndex: 'line_kind',
              width: 130,
              render: (v, line) => (
                <Select
                  size='small'
                  className='w-full'
                  value={v}
                  options={LINE_KIND_OPTIONS}
                  onChange={(value) =>
                    patchLine(line.key, { line_kind: value as DraftLine['line_kind'] })
                  }
                />
              )
            },
            {
              title: 'Securities',
              dataIndex: 'security_ids',
              width: 220,
              render: (v, line) => (
                <Select
                  size='small'
                  className='w-full'
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  placeholder='Link securities…'
                  value={(v as string[]) ?? []}
                  options={securityOptions}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <Button
                        type='text'
                        block
                        icon={<PlusOutlined />}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setAddSecurityForLineKey(line.key)}
                      >
                        New security…
                      </Button>
                    </>
                  )}
                  onChange={(value) => patchLineSecurities(line.key, (value as string[]) ?? [])}
                />
              )
            },
            {
              title: 'Issued',
              dataIndex: 'issued',
              width: 130,
              render: (v, line) => (
                <InputNumber
                  size='small'
                  className='w-full'
                  value={v ?? undefined}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                    patchLine(line.key, { issued: numberFromBlur(e) })
                  }
                />
              )
            },
            {
              title: 'FD',
              dataIndex: 'fully_diluted',
              width: 130,
              render: (v, line) => (
                <InputNumber
                  size='small'
                  className='w-full'
                  value={v ?? undefined}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                    patchLine(line.key, { fully_diluted: numberFromBlur(e) })
                  }
                />
              )
            },
            {
              title: 'Issue Price',
              dataIndex: 'issue_price',
              width: 120,
              render: (v, line) => (
                <InputNumber
                  size='small'
                  className='w-full'
                  value={v ?? undefined}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                    patchLine(line.key, { issue_price: numberFromBlur(e) })
                  }
                />
              )
            },
            {
              title: 'ESOP',
              dataIndex: 'is_esop_pool',
              width: 60,
              align: 'center' as const,
              render: (v, line) => (
                <Checkbox
                  checked={!!v}
                  onChange={(e) => patchLine(line.key, { is_esop_pool: e.target.checked })}
                />
              )
            },
            {
              title: '',
              key: 'remove',
              width: 40,
              render: (_, line) => (
                <Button
                  size='small'
                  type='text'
                  icon={<DeleteOutlined />}
                  onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                />
              )
            }
          ]}
        />
        <Button
          size='small'
          type='dashed'
          icon={<PlusOutlined />}
          onClick={() =>
            setLines((prev) => [
              ...prev,
              {
                key: nextKey(),
                security_ids: [],
                class_name: '',
                issued: null,
                fully_diluted: null,
                issue_price: null,
                is_esop_pool: false,
                line_kind: 'equity',
                holders: []
              }
            ])
          }
        >
          Add class
        </Button>
        {kind === 'round' &&
          roundId &&
          roundParticipations.some((p) => p.rounds__id === roundId) &&
          (() => {
            // Footer-level entry point for the co-investor import (the
            // per-line variant sits inside each line's holders expansion —
            // too buried to find, owner feedback 2026-08-14). Imports into
            // the chosen line, or creates the first class line on a fresh
            // capture.
            const targetKey =
              importTargetKey ??
              lines.find((l) => l.line_kind === 'equity')?.key ??
              lines[0]?.key ??
              null
            const target = lines.find((l) => l.key === targetKey)
            const count = target
              ? roundImportCandidates(target).length
              : roundParticipations.filter((p) => p.rounds__id === roundId).length
            return (
              <span className='inline-flex items-center gap-2 ml-2'>
                {lines.length > 1 && (
                  <Select
                    size='small'
                    className='w-44'
                    value={targetKey ?? undefined}
                    options={lines.map((l) => ({
                      value: l.key,
                      label: l.class_name || 'Unnamed class'
                    }))}
                    onChange={(v) => setImportTargetKey(v ?? null)}
                  />
                )}
                <Button
                  size='small'
                  type='dashed'
                  disabled={count === 0}
                  onClick={() => importRoundInto(targetKey)}
                >
                  Import round co-investors{count > 0 ? ` (${count})` : ' (all added)'}
                </Button>
              </span>
            )
          })()}
        <div className='text-xs text-gray-400'>
          Our own positions are not typed here — they are derived per fund from booked asset
          transactions and appear automatically in the view.
        </div>
      </div>
      <AddSecurityModal
        open={addSecurityForLineKey != null}
        companyId={accountId}
        onCreated={handleSecurityCreated}
        onCancel={() => setAddSecurityForLineKey(null)}
      />
      <AddCompanyModal
        open={addAccountFor != null}
        onCreated={handleCompanyCreated}
        onCancel={() => setAddAccountFor(null)}
      />
    </Modal>
  )
}
